import {
  ICreatePassportDto,
  IEquipment,
  IEquipmentDocument,
  IEquipmentEvent,
  IEquipmentEventInfo,
  IEquipmentMaintenanceBlank,
  IEquipmentPassport,
  IEquipmentPassportDataResponse,
  ILiftErrorCode,
  ILPConnectionRequest,
  ILKDSConnectionRequest,
  IPrevEquipmentPassportVersion,
  TCreateEquipmentDTO,
  TCreateEquipmentMaintenanceBlankDTO,
  TEquipmentMaintenanceBlankType,
} from './equipment.types'
import { HTTPResponse, IPaginationResponse } from '@/services/http/http.types.ts'
import http, { handleHttpError, handleHttpResponse } from '../../services/http'
import { IMaintenanceEvent } from '../Calendar/calendar.types'
import httpUtils from '@/services/http/http.utils.ts'

const getEquipmentById = (id: IEquipment['id']): Promise<HTTPResponse<IEquipment>> => {
  return http.get(`/nsi/v1/equipment/${id}`).then(handleHttpResponse).catch(handleHttpError)
}

const createEquipment = (dto: TCreateEquipmentDTO): Promise<HTTPResponse<IEquipment>> => {
  return http.post('/nsi/v1/equipment', dto).then(handleHttpResponse).catch(handleHttpError)
}

const updateEquipment = (id: IEquipment['id'], dto: TCreateEquipmentDTO): Promise<HTTPResponse<IEquipment>> => {
  return http.patch(`/nsi/v1/equipment/${id}`, dto).then(handleHttpResponse).catch(handleHttpError)
}

const resetEquipmentStatusFlag = (id: IEquipment['id']): Promise<HTTPResponse<IEquipment>> => {
  return http.post(`/nsi/v1/equipment/${id}/reset-status-flag`).then(handleHttpResponse).catch(handleHttpError)
}

const deleteEquipment = (id: IEquipment['id']): Promise<HTTPResponse<IEquipment>> => {
  return http.delete(`/nsi/v1/equipment/${id}`).then(handleHttpResponse).catch(handleHttpError)
}

type TGetEquipmentEventsParams = {
  page?: string
  limit?: number
  type?: string[]
  lpError?: string[]
  created_gte?: string
  created_lte?: string
}
const getEquipmentEvents = (
  id: IEquipment['id'],
  params: TGetEquipmentEventsParams
): Promise<HTTPResponse<IPaginationResponse<IEquipmentEvent>>> => {
  return http.get(`/nsi/v1/equipment/${id}/events`, { params }).then(handleHttpResponse).catch(handleHttpError)
}

type TGetEquipmentEventInfoParams = {
  errorCode: IEquipmentEvent['payload']['errorCode']
}

const getEquipmentEventInfo = (
  equipmentId: IEquipment['id'],
  params: TGetEquipmentEventInfoParams
): Promise<HTTPResponse<IEquipmentEventInfo>> => {
  return http
    .get(`/nsi/v1/equipment/${equipmentId}/events/analytics`, { params: { errorCode: params.errorCode } })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getEquipmentEventsAbortable = httpUtils.createAbortableRequest(
  async (
    signal,
    params: TGetEquipmentEventsParams & { equipmentId: IEquipment['id'] }
  ): Promise<HTTPResponse<IPaginationResponse<IEquipmentEvent>>> => {
    const { equipmentId, ...queryParams } = params

    return http
      .get(`/nsi/v1/equipment/${equipmentId}/events`, { params: queryParams, signal })
      .then(handleHttpResponse)
      .catch(handleHttpError)
  }
)

const getMaintenanceHistory = (
  id: IEquipment['id'],
  params: {
    page?: string
    limit?: number
    created_gte?: string
    created_lte?: string
    type?: TEquipmentMaintenanceBlankType
    search?: string
  }
): Promise<HTTPResponse<IPaginationResponse<IEquipmentMaintenanceBlank>>> => {
  return http
    .get(`/nsi/v1/equipment-maintenance/blanks/${id}`, { params })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getMaintenancePlansEquipment = (
  id: IEquipment['id'],
  params: {
    date_gte: string
    date_lte: string
  }
): Promise<HTTPResponse<IMaintenanceEvent[]>> => {
  return http
    .get(`/nsi/v1/maintenance/equipment-plan/${id}`, { params })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const createEquipmentMaintenanceBlank = (
  dto: TCreateEquipmentMaintenanceBlankDTO,
  files: File[] = []
): Promise<HTTPResponse<IEquipmentMaintenanceBlank>> => {
  if (files.length) {
    const formData = new FormData()
    for (const key in dto) {
      if (Array.isArray(dto[key])) {
        formData.set(key, JSON.stringify(dto[key]))
      } else {
        formData.set(key, dto[key])
      }
    }

    for (const file of files) {
      formData.append('files', file)
    }

    return http.post('/nsi/v1/equipment-maintenance/blanks', formData).then(handleHttpResponse).catch(handleHttpError)
  }

  return http.post('/nsi/v1/equipment-maintenance/blanks', dto).then(handleHttpResponse).catch(handleHttpError)
}

const putEquipmentPassport = (
  equipmentId: IEquipment['id'],
  passport: ICreatePassportDto
): Promise<HTTPResponse<IEquipmentPassport>> => {
  return http.put(`/nsi/v1/equipment/${equipmentId}/passport`, passport).then(handleHttpResponse).catch(handleHttpError)
}

const getEquipmentPassport = (passportId: number): Promise<HTTPResponse<IEquipmentPassport>> => {
  return http
    .get(`/nsi/v1/mdm/passports/instances/` + passportId)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getConnectionRequests = (): Promise<HTTPResponse<{ items: ILPConnectionRequest[] }>> => {
  return http.get('/nsi/v1/lifts/unknown-lifts').then(handleHttpResponse).catch(handleHttpError)
}

const getConnectionRequestsLKDS = (): Promise<HTTPResponse<{ items: ILKDSConnectionRequest[] }>> => {
  return http.get('/nsi/v1/lifts/unknown-lifts/lkds').then(handleHttpResponse).catch(handleHttpError)
}

const getLiftUptimeStats = (
  equipmentId: IEquipment['id']
): Promise<HTTPResponse<{ totalDownTime: number; upTimePercentage: number }>> => {
  return http
    .get('/nsi/v1/lifts/analytics/total-up-time/' + equipmentId)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftWarningsCount = (
  equipmentId: IEquipment['id'],
  dateFrom: string | number,
  dateTo: string | number
): Promise<HTTPResponse<{ count: number }>> => {
  return http
    .get(
      '/nsi/v1/lifts/analytics/warnings/' +
        equipmentId +
        `?date_from=${new Date(dateFrom).toISOString()}&date_to=${new Date(dateTo).toISOString()}`
    )
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftErrorsCount = (
  equipmentId: IEquipment['id'],
  dateFrom: string | number,
  dateTo: string | number
): Promise<HTTPResponse<{ count: number }>> => {
  return http
    .get(
      '/nsi/v1/lifts/analytics/errors/' +
        equipmentId +
        `?date_from=${new Date(dateFrom).toISOString()}&date_to=${new Date(dateTo).toISOString()}`
    )
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftFatalErrorsCount = (
  equipmentId: IEquipment['id'],
  dateFrom: string | number,
  dateTo: string | number
): Promise<HTTPResponse<{ count: number }>> => {
  return http
    .get(
      '/nsi/v1/lifts/analytics/critical-errors/' +
        equipmentId +
        `?date_from=${new Date(dateFrom).toISOString()}&date_to=${new Date(dateTo).toISOString()}`
    )
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftWarningsRating = (
  equipmentId: IEquipment['id'],
  dateFrom: string | number,
  dateTo: string | number
): Promise<HTTPResponse<Record<string, number>>> => {
  return http
    .get(
      '/nsi/v1/lifts/analytics/warnings-rating/' +
        equipmentId +
        `?date_from=${new Date(dateFrom).toISOString()}&date_to=${new Date(dateTo).toISOString()}`
    )
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftErrorsRating = (
  equipmentId: IEquipment['id'],
  dateFrom: string | number,
  dateTo: string | number
): Promise<HTTPResponse<Record<string, number>>> => {
  return http
    .get(
      '/nsi/v1/lifts/analytics/errors-rating/' +
        equipmentId +
        `?date_from=${new Date(dateFrom).toISOString()}&date_to=${new Date(dateTo).toISOString()}`
    )
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftFatalErrorsRating = (
  equipmentId: IEquipment['id'],
  dateFrom: string | number,
  dateTo: string | number
): Promise<HTTPResponse<Record<string, number>>> => {
  return http
    .get(
      '/nsi/v1/lifts/analytics/critical-errors-rating/' +
        equipmentId +
        `?date_from=${new Date(dateFrom).toISOString()}&date_to=${new Date(dateTo).toISOString()}`
    )
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getLiftErrorCodes = (equipmentId: IEquipment['id']): Promise<HTTPResponse<ILiftErrorCode[]>> => {
  return http.get(`/nsi/v1/lifts/${equipmentId}/error-codes`).then(handleHttpResponse).catch(handleHttpError)
}
const getHistoricalPassportData = (
  equipmentId: IEquipment['id'],
  passportVersionId: IPrevEquipmentPassportVersion['id']
): Promise<HTTPResponse<IEquipmentPassportDataResponse>> => {
  return http
    .get(`nsi/v1/equipment/${equipmentId}/passport-data/version/${passportVersionId}`)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}
const getPreviousPassportVersions = (
  equipmentId: IEquipment['id']
): Promise<HTTPResponse<IPrevEquipmentPassportVersion[]>> => {
  return http
    .get(`/nsi/v1/equipment/${equipmentId}/passport-data/versions`)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const listEquipmentDocuments = (
  equipmentId: IEquipment['id']
): Promise<HTTPResponse<IPaginationResponse<IEquipmentDocument>>> => {
  return http
    .get('/nsi/v1/equipment-documents?limit=100&equipmentId=' + equipmentId)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const createEquipmentDocument = (id: IEquipment['id'], document: File): Promise<HTTPResponse<IEquipmentDocument>> => {
  const fd = new FormData()
  fd.set('file', document)
  fd.set('equipmentId', id.toString())

  return http.post('/nsi/v1/equipment-documents', fd).then(handleHttpResponse).catch(handleHttpError)
}

const updateEquipmentDocument = (
  id: IEquipmentDocument['id'],
  dto: Pick<IEquipmentDocument, 'fileName'>
): Promise<HTTPResponse<IEquipmentDocument>> => {
  return http
    .patch('/nsi/v1/equipment-documents/' + id, dto)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const deleteEquipmentDocument = (id: IEquipmentDocument['id']): Promise<HTTPResponse<IEquipmentDocument>> => {
  return http
    .delete('/nsi/v1/equipment-documents/' + id)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const equipmentApi = {
  createEquipment,
  updateEquipment,
  resetEquipmentStatusFlag,
  deleteEquipment,
  getEquipmentById,
  getEquipmentEvents,
  getEquipmentEventInfo,
  getEquipmentEventsAbortable,
  getMaintenanceHistory,
  getMaintenancePlansEquipment,
  createEquipmentMaintenanceBlank,
  putEquipmentPassport,
  getEquipmentPassport,
  getConnectionRequests,
  getConnectionRequestsLKDS,
  getLiftUptimeStats,
  getLiftWarningsCount,
  getLiftErrorsCount,
  getLiftFatalErrorsCount,
  getLiftWarningsRating,
  getLiftErrorsRating,
  getLiftFatalErrorsRating,
  getLiftErrorCodes,
  getHistoricalPassportData,
  getPreviousPassportVersions,
  listEquipmentDocuments,
  createEquipmentDocument,
  updateEquipmentDocument,
  deleteEquipmentDocument,
}

export default equipmentApi
